import { AsyncPipe, DatePipe } from "@angular/common";
import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { DatepickerIntervalComponent } from "@components/datepicker-interval/datepicker-interval.component";
import { IconButtonComponent } from "@components/icon-button/icon-button.component";
import { IconComponent } from "@components/icon/icon.component";
import { InputComponent } from "@components/input/input.component";
import { ListColumnDirective } from "@components/list/list-column.directive";
import { ListComponent } from "@components/list/list.component";
import { PageComponent } from "@components/page/page.component";
import { RadiobuttonGroupComponent } from "@components/radiobuttongroup/radiobuttongroup.component";
import { SubmitButtonComponent } from "@components/submit-button/submit-button.component";
import { TreeSelectorComponent } from "@components/tree-selector/tree-selector.component";
import _ from "lodash";
import { BehaviorSubject } from "rxjs";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ToastService } from "~app/common/components/toast/toast.service";
import { SelectionItem } from "~app/common/components/tree-selector/selection-item";
import { CertifikatTyper, ConfirmDialogResponse, RaskRoller } from "~app/common/models/constants";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { CertifikatEditDialogComponent, CertifikatEditDialogData } from "./certifikat-edit-dialog/certifikat-edit-dialog.component";
import { CertifikatListaKontraktDialogComponent } from "./certifikat-lista-kontrakt-dialog/certifikat-lista-kontrakt-dialog.component";
import {
    CertifikatDto,
    CertifikatRadioButtonOption,
    CertifikatSearchFilter,
    CertifikatSearchFilterDto,
    certifikatRadioButtonItems
} from "./certifikat-models";
import { CertifikatService } from "./certifikat.service";

type CertifikatRow = CertifikatDto & {
    selected: boolean;
};

const DEFAULT_MODEL: CertifikatSearchFilter = {
    certifikatsnummer: "",
    certifikatstyp: [],
    agare: "",
    sveaskog: "alla",
    aktiv: "alla",
    certifikatFrom: undefined,
    certifikatTom: undefined
};

@Component({
    selector: "rask-certifikat",
    templateUrl: "./certifikat.component.html",
    styleUrls: ["./certifikat.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        PageComponent,
        FormsModule,
        ReactiveFormsModule,
        InputComponent,
        TreeSelectorComponent,
        RadiobuttonGroupComponent,
        SubmitButtonComponent,
        DatepickerIntervalComponent,
        ListComponent,
        ListColumnDirective,
        IconComponent,
        IconButtonComponent,
        AsyncPipe,
        DatePipe
    ]
})
export class CertifikatComponent implements OnInit {
    protected certifikat$ = new BehaviorSubject<CertifikatDto[]>([]);
    protected certifikatstyper: SelectionItem[] = CertifikatTyper.allaCertifierbaraTyper.map(x => ({
        value: x,
        text: x
    }));
    protected form: UntypedFormGroup;
    protected model = _.cloneDeep(DEFAULT_MODEL);
    protected radioButtonOptions = _.cloneDeep(certifikatRadioButtonItems);

    private readonly authenticationService = inject(AuthenticationService);
    private readonly busyService = inject(BusyService);
    private readonly certifikatService = inject(CertifikatService);
    private readonly formBinderService = inject(FormBinderService);
    private readonly modalService = inject(RaskModalService);
    private readonly toastService = inject(ToastService);

    ngOnInit(): void {
        this.form = this.formBinderService.CreateGroup(this.model);
    }

    protected kanVisaKontrakt() {
        return this.authenticationService.hasAnyRole([
            RaskRoller.role.Kopleda,
            RaskRoller.role.Kopplanera
        ]);
    }

    protected onCreate() {
        const data: CertifikatEditDialogData = { mode: "create" };
        const result$ = this.modalService.openAsync(
            "Lägg till certifikat", CertifikatEditDialogComponent,
            "lg", false, data
        );

        result$.subscribe((result: CertifikatDto) => {
            if (ObjectUtils.isNullOrUndefined(result)) {
                return;
            }

            this.create(result);
        });
    }

    protected onDelete(row: CertifikatRow) {
        const result$ = this.modalService.confirm(new ModalSettings("Ta bort certifikat",`Är du säker på att du vill ta bort certifikat ${row.certifikatsnummer}?`));

        result$.subscribe((result) => {
            if (result === ConfirmDialogResponse.cancel) {
                return;
            }

            this.delete(row);
        });
    }

    protected onOpen(event: Event, row: CertifikatRow) {
        event.preventDefault();

        // eslint-disable-next-line sonarjs/no-unused-vars
        const { selected: _selected, ...certifikat } = row;
        const data: CertifikatEditDialogData = {
            mode: "edit",
            certifikat
        };

        const result$ = this.modalService.openAsync(
            "Redigera certifikat", CertifikatEditDialogComponent,
            "lg", false, data
        );

        result$.subscribe((result: CertifikatDto) => {
            if (ObjectUtils.isNullOrUndefined(result)) {
                return;
            }

            this.update(result);
        });
    }

    protected onSearch() {
        const mapRadioButtonOption = (option: CertifikatRadioButtonOption) => {
            switch (option) {
                case "ja":
                    return true;
                case "nej":
                    return false;
                default:
                    return undefined;
            }
        };

        const filter: CertifikatSearchFilterDto = {
            ...this.model,
            sveaskog: mapRadioButtonOption(this.model.sveaskog),
            aktiv: mapRadioButtonOption(this.model.aktiv)
        };

        const result$ = this.busyService.addWork(
            this.certifikatService.getCertifikat(filter));
        result$.subscribe(x => this.certifikat$.next(x));
    }

    protected setFrom = (date: any) => {
        this.model.certifikatFrom = date;
    };

    protected setTom = (date: any) => {
        this.model.certifikatTom = date;
    };

    protected visaKontrakt(event: Event, row: CertifikatRow) {
        event.preventDefault();
        const result$ = this.busyService.addWork(
            this.certifikatService.getRelateradeKontrakt(row.id));

        result$.subscribe(res => {
            this.modalService.open(
                `Listar kopplade kontrakt till certifikat: ${row.certifikatsnummer} (${row.certifikatstyp})`,
                CertifikatListaKontraktDialogComponent, "xl", false, res, "certifikat-kontraktlista-modal"
            );
        });
    }

    private create(certifikat: CertifikatDto) {
        this.toastService.add(`Lade till certifikat ${certifikat.certifikatsnummer}`);
    }

    private delete(certifikat: CertifikatDto) {
        this.toastService.add(`Tog bort certifikat ${certifikat.certifikatsnummer}`);

        const response$ = this.busyService.addWork(
            this.certifikatService.deleteCertifikat(certifikat.id));

        response$.subscribe(() => {
            const updatedList = this.certifikat$.value.filter(
                x => x.id !== certifikat.id);
            this.certifikat$.next(updatedList);
        });
    }

    private update(certifikat: CertifikatDto) {
        this.toastService.add(`Uppdaterade certifikat ${certifikat.certifikatsnummer}`);

        const updatedList = this.certifikat$.value.map(
            x => x.id === certifikat.id ? { ...certifikat } : x);
        this.certifikat$.next(updatedList);
    }
}
